import React, {useEffect, useMemo, useState} from 'react';
import PageHeader from "app/main/shared-components/page-header/PageHeader";
import BaseTable from "app/main/shared-components/base-table/BaseTable";
import {Column} from "app/types/TableTypes";
import {useTranslation} from "react-i18next";
import { ActiveUsersService } from 'app/services';
import { ActiveUserResponse } from 'app/types/service-types/ActiveUsersTypes';
import DateUtils from "app/utils/DateUtils";
import {toast} from "react-toastify";

const OnlineUsersList = () => {
  const {t} = useTranslation();

  const [onlineUsers, setOnlineUsers] = useState<ActiveUserResponse[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    let isMounted = true;

    const fetchOnlineUsers = async (showErrorToast: boolean) => {
      try {
        if (isMounted) setIsLoading(true);
        const data = await ActiveUsersService.getOnlineUsers();
        if (!isMounted) return;
        setOnlineUsers(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!isMounted) return;
        if (showErrorToast) {
          toast.error(
            t(
              "app.main.employees.onlineUsers.error",
              "Failed to load online users."
            )
          );
        }
      } finally {
        if (!isMounted) return;
        setIsLoading(false);
      }
    };

    // Initial load
    fetchOnlineUsers(true);

    // Refresh periodically to keep the list up-to-date
    const intervalId = setInterval(() => fetchOnlineUsers(false), 10000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [t]);

  const columns: Column[] = useMemo(() => ([
    {
      Header: t("app.main.employees.onlineUsers.table.username", "Username"),
      accessor: "username",
    },
    {
      Header: t("app.main.employees.onlineUsers.table.status", "Online now"),
      accessor: "isOnline",
      Cell: ({cell}: {cell: {value: boolean}}) => {
        const isOnline = Boolean(cell.value);

        return (
          <span style={{color: isOnline ? "#2e7d32" : "#d32f2f", fontWeight: 600}}>
            {isOnline
              ? t("app.main.employees.onlineUsers.status.online", "Yes")
              : t("app.main.employees.onlineUsers.status.offline", "No")}
          </span>
        );
      },
    },
    {
      Header: t("app.main.employees.onlineUsers.table.loginTime", "Login time"),
      accessor: "loginTime",
      Cell: ({cell}: {cell: {value: string}}) => {
        const value = cell.value;
        return value ? DateUtils.formatDateTime(value) : "-";
      },
    },
    {
      Header: t("app.main.employees.onlineUsers.table.lastSeen", "Last seen"),
      accessor: "lastSeen",
      Cell: ({cell}: {cell: {value: string}}) => {
        const value = cell.value;
        return value ? DateUtils.formatDateTime(value) : "-";
      },
    },
    {
      Header: t("app.main.employees.onlineUsers.table.ip", "IP address"),
      accessor: "ipAddress",
    },
    {
      Header: t("app.main.employees.onlineUsers.table.logoutTime", "Logout time"),
      accessor: "logoutTime",
      Cell: ({cell}: {cell: {value: string | null}}) => {
        const value = cell.value;
        return value ? DateUtils.formatDateTime(value) : "-";
      },
    },
  ]), [t]);

  return (
    <div>
      <PageHeader
        title={t("app.main.employees.onlineUsers.title", "Online users")}
        desc={t(
          "app.main.employees.onlineUsers.desc",
          "List of clients that are currently logged into the system"
        )}
      />
      <BaseTable
        columns={columns}
        data={onlineUsers}
        isLoading={isLoading}
      />
    </div>
  );
};

export default OnlineUsersList;
